import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { canAccessStudyPlans } from '@/utils/study-plans'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/auth/callback',
      component: () => import('@/views/AuthCallbackView.vue'),
    },
    {
      path: '/login',
      component: () => import('@/views/LoginView.vue'),
    },
    {
      path: '/',
      component: () => import('@/views/AppShellView.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue'),
        },
        // Equipo
        {
          path: 'team',
          name: 'team-list',
          component: () => import('@/views/team/TeamListView.vue'),
        },
        {
          path: 'team/my-team',
          name: 'my-team',
          component: () => import('@/views/cpf/MyTeamView.vue'),
        },
        // CPF
        {
          path: 'cpf/framework',
          name: 'cpf-framework',
          component: () => import('@/views/cpf/CPFFrameworkView.vue'),
        },
        {
          path: 'cpf/me',
          name: 'cpf-me',
          component: () => import('@/views/cpf/MyCPFView.vue'),
        },
        {
          path: 'cpf/promotions',
          name: 'my-promotions',
          component: () => import('@/views/cpf/MyPromotionsView.vue'),
        },
        {
          path: 'cpf/pending-reviews',
          name: 'pending-reviews',
          component: () => import('@/views/cpf/PendingReviewsView.vue'),
        },
        {
          path: 'cpf/team',
          name: 'cpf-all-team',
          component: () => import('@/views/cpf/AllTeamCPFView.vue'),
        },
        {
          path: 'cpf/promotions-history',
          name: 'promotions-history',
          component: () => import('@/views/cpf/PromotionsHistoryView.vue'),
        },
        {
          path: 'cpf/:userId',
          name: 'cpf-detail',
          component: () => import('@/views/cpf/CPFDetailView.vue'),
        },
        {
          path: 'study-plans',
          name: 'study-plans',
          component: () => import('@/views/StudyPlansView.vue'),
          meta: { requiresStudyAccess: true },
        },
        {
          path: 'study-plans/:id',
          name: 'study-plan-detail',
          component: () => import('@/views/StudyPlanDetailView.vue'),
          meta: { requiresStudyAccess: true },
        },
        // Bitácora individual
        {
          path: 'log/:userId',
          name: 'developer-log',
          component: () => import('@/views/log/DeveloperLogView.vue'),
        },
        {
          path: 'cpf/:userId/promotion/new',
          name: 'promotion-request',
          component: () => import('@/views/cpf/PromotionRequestView.vue'),
        },
        {
          path: 'cpf/:userId/promotion/:requestId',
          name: 'promotion-detail',
          component: () => import('@/views/cpf/PromotionDetailView.vue'),
        },
        // Performance Reviews
        {
          path: 'reviews',
          name: 'reviews-list',
          component: () => import('@/views/reviews/ReviewsListView.vue'),
        },
        {
          path: 'reviews/:reviewId',
          name: 'review-detail',
          component: () => import('@/views/reviews/ReviewDetailView.vue'),
        },
        // Perfil del usuario (incluye los tokens para el MCP)
        {
          path: 'profile',
          name: 'profile',
          component: () => import('@/views/ProfileView.vue'),
        },
        // Org chart
        {
          path: 'org',
          name: 'org-chart',
          component: () => import('@/views/OrgChartView.vue'),
        },
        { path: 'achievements', name: 'achievements', component: () => import('@/views/achievements/AchievementsView.vue') },
        { path: 'achievements/users/:userId', name: 'achievement-profile', component: () => import('@/views/achievements/AchievementProfileView.vue') },
        { path: 'achievements/admin', name: 'achievements-admin', component: () => import('@/views/achievements/AchievementsAdminView.vue'), meta: { requiresAchievementsAdmin: true } },
        // IA
        {
          path: 'ai',
          redirect: '/ai/budget',
          meta: { requiresLeader: true },
        },
        {
          path: 'ai/budget',
          name: 'ai-budget',
          component: () => import('@/views/AIBudgetView.vue'),
          meta: { requiresLeader: true },
        },
        {
          path: 'ai/usage',
          name: 'ai-usage',
          component: () => import('@/views/AIUsageView.vue'),
          meta: { requiresLeader: true },
        },
        {
          path: 'ai-budget',
          redirect: '/ai/budget',
          meta: { requiresLeader: true },
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
})

const LEADER_ROLES = ['developer_leader', 'engineer_manager', 'head_of_engineering', 'cto']

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // En dev con bypass activo, inyectamos un usuario mock y dejamos pasar
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_USER_ID) {
    if (!auth.user) {
      const saved = sessionStorage.getItem('dev_user_override')
      if (saved) {
        try {
          const { id, name, role, email } = JSON.parse(saved)
          auth.user = { id, email: email ?? 'dev@alegra.com', name: name || 'Dev User', role }
        } catch {
          // Si el JSON está corrupto, limpiamos y usamos defaults
          sessionStorage.removeItem('dev_user_override')
          auth.user = {
            id:    import.meta.env.VITE_DEV_USER_ID,
            email: 'dev@alegra.com',
            name:  'Dev User',
            role:  import.meta.env.VITE_DEV_ROLE ?? 'developer_leader',
          }
        }
      } else {
        auth.user = {
          id:    import.meta.env.VITE_DEV_USER_ID,
          email: 'dev@alegra.com',
          name:  'Dev User',
          role:  import.meta.env.VITE_DEV_ROLE ?? 'developer_leader',
        }
      }
    }
    if (to.meta.requiresStudyAccess && !canAccessStudyPlans(auth.user?.role ?? '')) return { path: '/' }
    return
  }

  // Modo testing (HoE): pre-cargar usuario simulado desde sessionStorage
  // para evitar que el frontend haga peticiones como el HoE real
  const simRaw = sessionStorage.getItem('simulate_user')
  if (simRaw) {
    try {
      const { id, name, role, email } = JSON.parse(simRaw)
      if (id && !auth.user) {
        auth.user = { id, email: email ?? 'dev@alegra.com', name: name || 'Simulated User', role }
      }
    } catch { /* silent */ }
  }

  // Token guardado pero usuario no cargado aún (recarga de página)
  if (auth.token && !auth.user) {
    await auth.initialize()
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { path: '/login' }
  }
  if (to.meta.requiresStudyAccess && !canAccessStudyPlans(auth.user?.role ?? '')) return { path: '/' }
  if (to.meta.requiresLeader && auth.user && !LEADER_ROLES.includes(auth.user.role)) {
    return { path: '/' }
  }
  if (to.meta.requiresAchievementsAdmin && auth.user && !['head_of_engineering','cto'].includes(auth.user.role)) return { path: '/achievements' }
})

export default router
